import { useEffect, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { useExperiments } from '@alltrails/experiments';
import { useAppSelector } from '../redux';

/**
 * Returns true only when the map exists and its style has finished loading.
 * Use this to avoid calling map.addSource() or reading style state before the
 * style is ready (e.g. after switching to a new mapbox style ID), which would
 * throw "Style is not done loading".
 */
const useMapStyleLoaded = (map: MapRef | undefined): boolean => {
  const [isLoaded, setIsLoaded] = useState(() => map?.isStyleLoaded() ?? false);
  const baseStyleId = useAppSelector(state => state.map.baseStyleId);
  const baseStyleIdRef = useRef(baseStyleId);
  const styleChangeInProgressRef = useRef(false);
  const debugEnabled = useExperiments()['mugen-explore-debug']?.value === 'on';

  useEffect(() => {
    if (baseStyleIdRef.current !== baseStyleId) {
      if (debugEnabled) {
        console.log('[useMapStyleLoaded] baseStyleId changed:', baseStyleIdRef.current, '->', baseStyleId);
      }
      styleChangeInProgressRef.current = true;
      setIsLoaded(false);
      baseStyleIdRef.current = baseStyleId;
    }
  }, [baseStyleId, debugEnabled]);

  useEffect(() => {
    if (!map) {
      if (debugEnabled) {
        console.log('[useMapStyleLoaded] map is undefined');
      }
      setIsLoaded(false);
      return;
    }

    let cancelled = false;

    const trySetLoaded = () => {
      if (baseStyleIdRef.current !== baseStyleId) return;
      if (!map.isStyleLoaded()) return;
      if (debugEnabled) {
        console.log('[useMapStyleLoaded] trySetLoaded: isLoaded=true');
      }
      setIsLoaded(true);
    };

    // Defer "ready" until after the next paint so Mapbox has committed the style.
    // Only require baseStyleId match at schedule time; isStyleLoaded() is re-checked in rAFs (it can lag after style.load).
    // Reduces race with react-map-gl Source/Layer (see visgl/react-map-gl#2377).
    const deferSetLoaded = () => {
      if (debugEnabled) {
        console.log('[useMapStyleLoaded] deferSetLoaded called');
      }
      if (baseStyleIdRef.current !== baseStyleId) return;
      const maxFrames = 30; // ~500ms at 60fps; isStyleLoaded() can lag after style.load for non-Standard styles
      const trySetLoadedInFrame = (frame: number) => {
        if (cancelled) return;
        if (baseStyleIdRef.current !== baseStyleId) return;
        if (map.isStyleLoaded()) {
          if (debugEnabled) {
            console.log('[useMapStyleLoaded] deferSetLoaded rAF frame', frame, ': style loaded, isLoaded=true');
          }
          styleChangeInProgressRef.current = false;
          setIsLoaded(true);
          return;
        }
        if (frame < maxFrames) {
          requestAnimationFrame(() => trySetLoadedInFrame(frame + 1));
        } else {
          if (debugEnabled) {
            console.log('[useMapStyleLoaded] deferSetLoaded: exhausted', maxFrames, 'frames, falling through');
          }
          styleChangeInProgressRef.current = false;
          setIsLoaded(true);
        }
      };
      requestAnimationFrame(() => trySetLoadedInFrame(0));
    };

    const onMapLoad = () => {
      if (debugEnabled) {
        console.log('[useMapStyleLoaded] "load" event fired');
      }
      if (baseStyleIdRef.current === baseStyleId) {
        deferSetLoaded();
      }
    };
    const onStyleLoad = () => {
      if (debugEnabled) {
        console.log('[useMapStyleLoaded] "style.load" event fired');
      }
      // Schedule deferred check even when isStyleLoaded() is false here; it often lags for non-Standard styles.
      if (baseStyleIdRef.current === baseStyleId) {
        deferSetLoaded();
      }
    };

    // Fallback for styles (e.g. Mapbox Standard / alltrailsOutdoorsV3) where isStyleLoaded() never becomes true.
    const onIdle = () => {
      if (debugEnabled) {
        console.log('[useMapStyleLoaded] "idle" event fired');
      }
      if (cancelled) return;
      if (baseStyleIdRef.current !== baseStyleId) return;
      requestAnimationFrame(() => {
        if (cancelled) return;
        if (baseStyleIdRef.current === baseStyleId) {
          if (debugEnabled) {
            console.log('[useMapStyleLoaded] "idle" rAF fallback: isLoaded=true');
          }
          styleChangeInProgressRef.current = false;
          setIsLoaded(true);
        }
      });
    };

    if (styleChangeInProgressRef.current) {
      if (debugEnabled) {
        console.log('[useMapStyleLoaded] styleChangeInProgress — waiting for events');
      }
      // Style just changed: do not trust map.isStyleLoaded() (it's the old style). Wait for load/style.load.
      styleChangeInProgressRef.current = false;
      map.once('load', onMapLoad);
      map.once('style.load', onStyleLoad);
      map.once('idle', onIdle);
    } else if (map.isStyleLoaded()) {
      console.log('[useMapStyleLoaded] already loaded — calling trySetLoaded');
      trySetLoaded();
    } else {
      console.log('[useMapStyleLoaded] style not loaded — waiting for events');
      map.once('load', onMapLoad);
      map.once('style.load', onStyleLoad);
      map.once('idle', onIdle);
    }

    return () => {
      console.log('[useMapStyleLoaded] cleanup');
      cancelled = true;
      map.off('load', onMapLoad);
      map.off('style.load', onStyleLoad);
      map.off('idle', onIdle);
    };
  }, [map, baseStyleId, debugEnabled]);

  return isLoaded;
};

export default useMapStyleLoaded;
